/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { faGear } from '@fortawesome/free-solid-svg-icons/faGear';
import { Maybe } from 'purify-ts/Maybe';
import Multiselect from 'vue-multiselect';
import p from 'vue-strict-prop';
import * as tsx from 'vue-tsx-support';
import * as comp from '@/components';
import { makeInputValue } from '@/components/Editors';
import * as models from '@/models';
import { PreferenceStore } from '@/store';
import * as utils from '@/utils';
import { SHOW_INLINE_FEEDBACKS } from '@/utils/props';
const MIN_LOAD_TIME = 200;
export default tsx.componentFactoryOf().create({
    name: 'preference-manager',
    props: {
        fileId: p(String).optional,
        showWhitespace: p(Boolean).default(true),
        showWhitespacePreset: p(Boolean).default(false),
        showLanguage: p(Boolean).default(true),
        showFontSize: p(Boolean).default(true),
        showInlineFeedback: p(Boolean).default(true),
        showTheme: p(Boolean).default(true),
        showContextAmount: p(Boolean).default(false),
        showTabPreset: p(Boolean).default(false),
        inPopover: p(Boolean).default(false),
        boundary: p(String).default('window'),
        container: p(String).optional,
        placement: p(String).default('bottom'),
        tableWidth: p(String).default('100%'),
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const uniqueId = utils.getUniqueId();
        return {
            uniqueId,
            loading: true,
            themePromise: null,
            contextAmountPromise: null,
            fontSizePromise: null,
            tabPresetPromise: null,
            languagePromise: null,
            whitespacePromise: null,
            whitespacePresetPromise: null,
            selectedLanguage: 'Default',
            popoverDisabled: false,
            languages: Maybe.empty(),
            dropdownShowing: 0,
        };
    },
    computed: {
        /** @returns The id of the preference manager button that opens the settings modal/popover. */
        btnId() {
            return `settings-toggle-${this.uniqueId}`;
        },
        /** @returns The id of the preference manager popover. */
        popoverId() {
            return `settings-popover-${this.uniqueId}`;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fontSize() {
            return PreferenceStore.fontSize();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        contextAmount() {
            return PreferenceStore.contextAmount();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        theme() {
            return models.Theme.get();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        tabPreset() {
            return models.SubmissionTabPreset.get();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespace() {
            if (this.fileId == null) {
                return this.whitespacePreset;
            }
            return models.WhitespaceForFile.get(this.fileId);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespacePreset() {
            return models.WhitespacePreset.get();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        inlineFeedback() {
            switch (this.$route.query.showInlineFeedback) {
                case 'none':
                case 'false':
                    return 'none';
                case 'only-normal':
                    return 'only-normal';
                case 'only-peer':
                    return 'only-peer';
                case 'all':
                default:
                    return 'all';
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        settingsContentNode() {
            return utils.ifExpr(this.loading, () => <comp.Loader />, () => this.settingsTableNode);
        },
        /** @returns The settings that are available based on the component's props. */
        settingsTableNode() {
            return (<table ref="settings-table" style={{ width: this.tableWidth }} class="preference-manager-table table mb-0 text-left" tabindex={this.inPopover ? 0 : -1}>
                    <tbody>
                        {this.whitespaceNode}
                        {this.whitespacePresetNode}
                        {this.languageNode}
                        {this.fontSizeNode}
                        {this.inlineFeedbackNode}
                        {this.contextAmountNode}
                        {this.themeNode}
                        {this.tabPresetNode}
                    </tbody>
                </table>);
        },
        /** @returns The boolean toggle for whether whitespaces should be shown in the current code viewer. */
        whitespaceNode() {
            const toggleId = `whitespace-toggle-${this.uniqueId}`;
            return this.renderTableRow(this.showWhitespace, () => <label for={toggleId}>Whitespace (current file)</label>, this.whitespacePromise, () => (<comp.Editors.BooleanToggle id={toggleId} value={Maybe.of(this.whitespace)} onInput={({ value }) => this.whitespaceChanged(value)} labelOn="Show" labelOff="Hide" 
            // @ts-expect-error: For some reason, TypeScript does not see tabindex as an attribute.
            tabindex={0}/>));
        },
        /** @returns The boolean toggle for whether whitespaces should be shown by default in the code viewer. */
        whitespacePresetNode() {
            const toggleId = `whitespace-preset-toggle-${this.uniqueId}`;
            return this.renderTableRow(this.showWhitespacePreset, () => [
                <label for={toggleId}>Whitespace (default)</label>,
                ' ',
                <comp.DescriptionPopover hugText>
                        <p class="mb-2">
                            The default value for the whitespace setting in the code viewer. The
                            value can be overridden per file.
                        </p>
                        <p class="mb-2">
                            <b>Hide</b> to hide whitespace by default.
                        </p>
                        <p class="mb-2">
                            <b>Show</b> to show whitespace by default.
                        </p>
                    </comp.DescriptionPopover>,
            ], this.whitespacePresetPromise, () => (<comp.Editors.BooleanToggle id={toggleId} value={Maybe.of(this.whitespacePreset)} onInput={({ value }) => this.whitespacePresetChanged(value)} labelOn="Show" labelOff="Hide" 
            // @ts-expect-error: For some reason, TypeScript does not see tabindex as an attribute.
            tabindex={0}/>));
        },
        /** @returns The multiselect input for selecting the programming language. */
        languageNode() {
            const labelId = `language-label-${this.uniqueId}`;
            return this.renderTableRow(this.showLanguage, () => <span id={labelId}>Language</span>, this.languagePromise, () => {
                this.loadLanguages();
                return this.languages.caseOf({
                    Nothing: () => <comp.Loader scale={1}/>,
                    Just: languages => (<Multiselect 
                    // @ts-expect-error: This component doesn't declare its props.
                    value={this.selectedLanguage} onInput={(lang) => this.languageChanged(lang)} hideSelected={this.selectedLanguage === 'Default'} deselectLabel="Reset language" selectLabel="Select language" options={languages} onOpen={() => this.disablePopover()} onClose={() => this.enablePopover()} aria-labelledby={labelId} tabindex={this.inPopover ? -1 : 0}/>),
                });
            });
        },
        /** @returns The number input for the code font size. */
        fontSizeNode() {
            const toggleId = `font-size-toggle-${this.uniqueId}`;
            return this.renderTableRow(this.showFontSize, () => <label for={toggleId}>Code font size</label>, this.fontSizePromise, () => (<b-input-group append="px">
                        <comp.Editors.Number id={toggleId} name="Code font size" value={makeInputValue(this.fontSize)} onInput={fontSize => this.fontSizeChanged(fontSize)} class="form-control" step={1} min={1} tabindex={0}/>
                    </b-input-group>));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        inlineFeedbackNode() {
            const labelId = `inline-feedback-label-${this.uniqueId}`;
            return this.renderTableRow(this.showInlineFeedback, () => <span id={labelId}>Inline feedback</span>, null, () => this.renderDropdown(SHOW_INLINE_FEEDBACKS, this.inlineFeedback, this.inlineFeedbackText, val => this.emitInlineFeedback(val, true), labelId));
        },
        /** @returns The number input for the amount of context lines to be shown for inline feedback messages. */
        contextAmountNode() {
            const toggleId = `context-amount-label-${this.uniqueId}`;
            return this.renderTableRow(this.showContextAmount, () => <label for={toggleId}>Amount of context</label>, this.contextAmountPromise, () => (<comp.Editors.Number name="Amount of context" value={makeInputValue(this.contextAmount)} onInput={contextAmount => this.contextAmountChanged(contextAmount)} class="form-control" step={1} min={0} id={toggleId} tabindex={0}/>));
        },
        /** @returns The dropdown input for the page's theme. */
        themeNode() {
            const labelId = `theme-label-${this.uniqueId}`;
            return this.renderTableRow(this.showTheme, () => [
                <span id={labelId}>Theme</span>,
                ' ',
                <comp.DescriptionPopover hugText>
                        <p class="mb-2">
                            <b>Light</b> to always use the light theme.
                        </p>
                        <p class="mb-2">
                            <b>Dark</b> to always use the dark theme.
                        </p>
                        <p class="mb-0">
                            <b>System</b> to use the system configured theme.
                        </p>
                    </comp.DescriptionPopover>,
            ], this.themePromise, () => this.renderDropdown(['light', 'dark', 'auto'], this.theme, this.themeText, this.themeChanged, labelId));
        },
        /** @returns The dropdown input for the tab preset. */
        tabPresetNode() {
            const labelId = `tab-preset-label-${this.uniqueId}`;
            return this.renderTableRow(this.showTabPreset, () => [
                <span id={labelId}>Submission tab selection</span>,
                ' ',
                <comp.DescriptionPopover hugText>
                        <p class="mb-2">The default tab to select when going to a submission.</p>

                        <p class="mb-2">
                            <b>Smart</b> selects a tab based on the state of the assignment and
                            AutoTest.
                        </p>

                        <p>
                            <b>Code</b>, <b>Feedback Overview</b>, <b>AutoTest</b>, and
                            <b>Peer Feedback</b> all go to the corresponding tab.
                        </p>
                    </comp.DescriptionPopover>,
            ], this.tabPresetPromise, () => this.renderDropdown(['smart', 'code', 'feedback-overview', 'auto-test', 'peer-feedback'], this.tabPreset, this.tabPresetText, this.tabPresetChanged, labelId));
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        themeChanged(val) {
            this.themePromise = models.Theme.set(val)
                .map(response => {
                tsx.emitOn(this, 'onTheme', val);
                return response.addCallbacks({
                    onAfterSuccess: () => {
                        this.themePromise = null;
                    },
                });
            })
                .run();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        tabPresetChanged(val) {
            this.tabPresetPromise = models.SubmissionTabPreset.set(val)
                .map(response => {
                tsx.emitOn(this, 'onTabPreset', val);
                return response.addCallbacks({
                    onAfterSuccess: () => {
                        this.tabPresetPromise = null;
                    },
                });
            })
                .run();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        contextAmountChanged(val) {
            val.caseOf({
                Left: err => {
                    this.contextAmountPromise = Promise.reject(err);
                },
                Right: maybeValue => maybeValue.ifJust(contextAmount => {
                    PreferenceStore.setContextAmount(contextAmount);
                    tsx.emitOn(this, 'onContextAmount', contextAmount);
                    this.contextAmountPromise = Promise.resolve({
                        onAfterSuccess: () => {
                            this.contextAmountPromise = null;
                        },
                    });
                }),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fontSizeChanged(val) {
            val.caseOf({
                Left: err => {
                    this.fontSizePromise = Promise.reject(err);
                },
                Right: maybeValue => maybeValue.ifJust(fontSize => {
                    PreferenceStore.setFontSize(fontSize);
                    tsx.emitOn(this, 'onFontSize', fontSize);
                    this.fontSizePromise = Promise.resolve({
                        onAfterSuccess: () => {
                            this.contextAmountPromise = null;
                        },
                    });
                }),
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespaceChanged(val) {
            if (this.fileId == null) {
                utils.sentry.withSentry(sentry => {
                    sentry.captureMessage('No file id is set.');
                });
                return;
            }
            this.whitespacePromise = models.WhitespaceForFile.set(this.fileId, val)
                .map(response => response.addCallbacks({
                onAfterSuccess: () => {
                    this.whitespacePromise = null;
                },
            }))
                .run();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespacePresetChanged(val) {
            this.whitespacePresetPromise = models.WhitespacePreset.set(val)
                .map(response => {
                tsx.emitOn(this, 'onWhitespacePreset', val);
                return response.addCallbacks({
                    onAfterSuccess: () => {
                        this.whitespacePresetPromise = null;
                    },
                });
            })
                .run();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        languageChanged(lang) {
            if (this.fileId == null) {
                utils.sentry.withSentry(sentry => {
                    sentry.captureMessage('No file id is set.');
                });
                return;
            }
            if (lang == null) {
                this.selectedLanguage = 'Default';
            }
            else {
                this.selectedLanguage = lang;
            }
            this.languagePromise = this.$hlanguageStore
                .setItem(this.fileId, this.selectedLanguage)
                .then(() => ({
                onAfterSuccess: () => {
                    this.languagePromise = null;
                },
            }));
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadValues(fileId) {
            this.loading = true;
            this.emitInlineFeedback(this.inlineFeedback, false);
            await Promise.all([
                this.loadWhitespace(fileId),
                this.loadLanguage(fileId),
                this.loadUserPreferences(),
            ]);
            this.loading = false;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        loadWhitespace(fileId) {
            if (!this.showWhitespace || fileId == null) {
                return Promise.resolve();
            }
            return models.WhitespaceForFile.load(fileId)
                .ifRight(() => {
                this.emitWhitespace();
            })
                .ifLeft(err => {
                this.whitespacePromise = Promise.reject(err);
            })
                .run();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emitWhitespace() {
            if (this.fileId != null) {
                tsx.emitOn(this, 'onWhitespace', models.WhitespaceForFile.get(this.fileId));
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadLanguage(fileId) {
            if (this.showLanguage === true && fileId != null) {
                await this.$hlanguageStore.getItem(fileId).then(lang => {
                    if (fileId === this.fileId) {
                        this.selectedLanguage = lang ?? 'Default';
                    }
                }, err => {
                    this.languagePromise = Promise.reject(err);
                });
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async loadUserPreferences() {
            const loaders = [];
            if (this.showTheme) {
                loaders.push(models.Theme.load().chainLeft(err => {
                    this.themePromise = Promise.reject(err);
                    return utils.emptyResponse();
                }));
            }
            if (this.showTabPreset) {
                loaders.push(models.SubmissionTabPreset.load().chainLeft(err => {
                    this.tabPresetPromise = Promise.reject(err);
                    return utils.emptyResponse();
                }));
            }
            if (this.showWhitespacePreset || this.showWhitespace) {
                loaders.push(models.WhitespacePreset.load().chainLeft(err => {
                    this.whitespacePresetPromise = Promise.reject(err);
                    return utils.emptyResponse();
                }));
            }
            await utils.eitherAsyncUtils.all(loaders).run();
        },
        // Disable the popover so a blur event on an element in it will not
        // cause it to close.
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        disablePopover() {
            this.popoverDisabled = true;
        },
        // Enable the popover again. Make sure this happens _after_ the blur
        // event on elements inside it. We focus the button before enabling
        // the popover again to make sure that at least something is focused
        // that, when blurred, will cause the popover to close.
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async enablePopover() {
            // @ts-expect-error cannot prove that this.$el is an HTMLElement
            // with a focus method.
            this.$el.focus();
            await this.$afterRerender();
            this.popoverDisabled = false;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        emitInlineFeedback(show, freshChange) {
            tsx.emitOn(this, 'onInlineFeedback', { show, freshChange });
            if (this.inlineFeedback !== show) {
                const query = { ...this.$route.query };
                if (show === 'all') {
                    delete query.showInlineFeedback;
                }
                else {
                    query.showInlineFeedback = show;
                }
                this.$router.replace({ ...this.$route, query });
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        inlineFeedbackText(val) {
            switch (val) {
                case 'all':
                    return 'Show All';
                case 'only-normal':
                    return 'Only Normal';
                case 'only-peer':
                    return 'Only Peer';
                case 'none':
                    return 'Hide All';
                default:
                    throw utils.AssertionError.assertNever(val);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        themeText(val) {
            switch (val) {
                case 'light':
                    return 'Light';
                case 'dark':
                    return 'Dark';
                case 'auto':
                    return 'System';
                default:
                    throw utils.AssertionError.assertNever(val);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        tabPresetText(val) {
            switch (val) {
                case 'smart':
                    return 'Smart selection';
                case 'code':
                    return 'Code tab';
                case 'feedback-overview':
                    return 'Feedback Overview tab';
                case 'auto-test':
                    return 'AutoTest tab';
                case 'peer-feedback':
                    return 'Peer Feedback tab';
                default:
                    throw utils.AssertionError.assertNever(val);
            }
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        renderTableRow(cond, name, loader, renderInput) {
            return utils.ifOrEmpty(cond === true, () => (<tr>
                    <td class="align-middle shrink">{name()}</td>
                    <td class="align-middle shrink">
                        <comp.PromiseLoader promise={loader} waitAtLeast={MIN_LOAD_TIME} popoverPlacement="top" onError={() => this.disablePopover()} onAfterError={() => this.enablePopover()}/>
                    </td>
                    <td class="align-middle">{renderInput()}</td>
                </tr>));
        },
        /**
         * Renders a dropdown node.
         *
         * @param values The items of the dropdown.
         * @param active Which item of the dropdown is selected.
         * @param getName A function that maps an item to it's name.
         * @param onInput A callback for when the item is selected.
         * @returns The rendered dropdown.
         */
        renderDropdown(values, active, getName, onInput, labelledBy) {
            return (<b-dropdown class="w-100 p-0 no-center" menu-class="w-100" onHide={() => {
                    this.dropdownShowing--;
                }} onShow={() => {
                    this.dropdownShowing++;
                }} scopedSlots={{
                    'button-content': () => getName(active),
                }} toggle-attrs={{
                    tabindex: 0,
                    'aria-lablledby': labelledBy,
                }}>
                    {values.map(val => (<b-dropdown-item active={active === val} onClick={() => onInput(val)}>
                            {getName(val)}
                        </b-dropdown-item>))}
                </b-dropdown>);
        },
        /** Load all available languages if they are not yet loaded. */
        loadLanguages() {
            this.languages.ifNothing(async () => {
                const { listLanguages } = await import('@/utils/highlight');
                const langs = listLanguages();
                langs.push('plain');
                langs.sort(utils.cmpNoCase);
                langs.unshift('Default');
                this.languages = Maybe.of(langs);
                return langs;
            });
        },
        /** Focuses on the popover if it is visible. */
        async focusPopover() {
            await this.$afterRerender();
            this.$withRef('settings-table', el => utils.focusOn(el));
        },
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    mounted() {
        this.loadValues(this.fileId);
    },
    watch: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespace() {
            this.emitWhitespace();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        whitespacePreset() {
            this.emitWhitespace();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        selectedLanguage() {
            tsx.emitOn(this, 'onLanguage', this.selectedLanguage);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showWhitespace() {
            this.loadWhitespace(this.fileId);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showLanguage() {
            this.loadLanguage(this.fileId);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        fileId(newVal, oldVal) {
            if (newVal != null && newVal !== oldVal) {
                this.loadValues(newVal);
            }
        },
        /**
         * Triggered when the dropdown showing changes.
         *
         * When no dropdowns are showing anymore we should focus the popover table again to make it
         * possible to hide it easily.
         */
        dropdownShowing() {
            if (this.dropdownShowing === 0) {
                this.$withRef('settings-table', el => {
                    el.focus?.();
                });
            }
        },
    },
    /** @returns The rendered preference manager component. */
    render() {
        return utils.ifExpr(this.inPopover === true, () => (<b-btn id={this.btnId} class="preference-manager settings-toggle" v-b-popover_hover_focus_top="Settings" aria-label="Submission settings" onClick={() => {
                this.focusPopover();
            }}>
                    <comp.Icon icon={faGear}/>
                    <b-popover triggers={this.dropdownShowing === 0 ? 'click blur' : 'click'} custom-class="overflow-visible" id={this.popoverId} target={this.btnId} placement={this.placement} boundary={this.boundary} container={this.container} disabled={this.popoverDisabled} onShow={() => this.$root.$emit('bv::hide::popover')}>
                        {this.settingsContentNode}
                    </b-popover>
                </b-btn>), () => <div class="preference-manager">{this.settingsContentNode}</div>);
    },
});
